// FILE: singleChatPanelStore.ts
// Purpose: Persist right-panel state for single-thread chat surfaces.
// Layer: UI state store
// Exports: single-surface panel store, default-state helpers, and selectors.

import type { ThreadId } from "@taskin-code/contracts";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  createDefaultChatPanelState,
  sanitizeChatPanelState,
  type ChatPanelStateFields,
} from "./chatPanelPersistence";

export type SingleChatPanelState = ChatPanelStateFields;

interface SingleChatPanelStore {
  panelStateByThreadId: Record<string, SingleChatPanelState | undefined>;
  setThreadPanelState: (threadId: ThreadId, patch: Partial<SingleChatPanelState>) => void;
  clearThreadPanelState: (threadId: ThreadId) => void;
}

const SINGLE_CHAT_PANEL_STORAGE_KEY = "t3code:single-chat-panel-state:v1";
const SINGLE_CHAT_PANEL_STORAGE_VERSION = 1;

export function createDefaultSingleChatPanelState(): SingleChatPanelState {
  return createDefaultChatPanelState();
}

// Runs on every rehydration, not just on a version bump: the value we defend against is a
// forward-written panel name from a newer client, which carries no version change of its own.
export function sanitizePersistedSingleChatPanelStoreState(
  persistedState: unknown,
): Pick<SingleChatPanelStore, "panelStateByThreadId"> {
  if (!persistedState || typeof persistedState !== "object") {
    return { panelStateByThreadId: {} };
  }
  const rawMap = (persistedState as Record<string, unknown>).panelStateByThreadId;
  if (!rawMap || typeof rawMap !== "object") {
    return { panelStateByThreadId: {} };
  }

  const panelStateByThreadId: Record<string, SingleChatPanelState | undefined> = {};
  for (const [threadId, value] of Object.entries(rawMap as Record<string, unknown>)) {
    if (threadId.length === 0) {
      continue;
    }
    const sanitized = sanitizeChatPanelState(value);
    if (!sanitized) {
      continue;
    }
    panelStateByThreadId[threadId] = sanitized;
  }
  return { panelStateByThreadId };
}

const DEFAULT_SINGLE_CHAT_PANEL_STATE = createDefaultSingleChatPanelState();

function getDefaultSingleChatPanelState(): SingleChatPanelState {
  return DEFAULT_SINGLE_CHAT_PANEL_STATE;
}

export const useSingleChatPanelStore = create<SingleChatPanelStore>()(
  persist(
    (set) => ({
      panelStateByThreadId: {},
      setThreadPanelState: (threadId, patch) =>
        set((state) => {
          const previous = state.panelStateByThreadId[threadId] ?? getDefaultSingleChatPanelState();
          const next = {
            ...previous,
            ...patch,
          };
          if (
            previous.panel === next.panel &&
            previous.diffTurnId === next.diffTurnId &&
            previous.diffFilePath === next.diffFilePath &&
            previous.hasOpenedPanel === next.hasOpenedPanel &&
            previous.lastOpenPanel === next.lastOpenPanel &&
            previous.sideChatThreadId === next.sideChatThreadId
          ) {
            return state;
          }
          return {
            panelStateByThreadId: {
              ...state.panelStateByThreadId,
              [threadId]: next,
            },
          };
        }),
      clearThreadPanelState: (threadId) =>
        set((state) => {
          if (!Object.hasOwn(state.panelStateByThreadId, threadId)) {
            return state;
          }
          const nextPanelStateByThreadId = { ...state.panelStateByThreadId };
          delete nextPanelStateByThreadId[threadId];
          return {
            panelStateByThreadId: nextPanelStateByThreadId,
          };
        }),
    }),
    {
      name: SINGLE_CHAT_PANEL_STORAGE_KEY,
      version: SINGLE_CHAT_PANEL_STORAGE_VERSION,
      storage: createJSONStorage(() => localStorage),
      migrate: (persistedState) => sanitizePersistedSingleChatPanelStoreState(persistedState),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...sanitizePersistedSingleChatPanelStoreState(persistedState),
      }),
    },
  ),
);

export function selectSingleChatPanelState(threadId: ThreadId) {
  return (store: SingleChatPanelStore) =>
    // Keep the fallback snapshot stable so React does not observe a phantom store change
    // while mounting a thread that has no persisted panel state yet.
    store.panelStateByThreadId[threadId] ?? getDefaultSingleChatPanelState();
}
